import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Landing Page
    hero_title: "Your Plan. Built for You. Only You.",
    hero_subtitle: "Elite performance programs, personalized to you. Powered by science.",
    hero_desc: "Every program is 100% unique—custom-built for your body, your goals, your schedule, and your life. We create it. You crush it.",
    get_started: "Get Started",
    login: "Log In",
    start_free_trial: "Start Free Trial",
    watch_video: "Watch Video",
    
    // Navigation
    about: "About",
    science: "Science",
    dashboard: "Dashboard",
    training: "Training",
    nutrition: "Nutrition",
    progress: "Progress",
    challenges: "Challenges",
    messages: "Messages",
    profile: "Profile",
    logout: "Log Out",
    
    // For Everyone Section
    built_for_everyone: "Built for Every Body",
    everyone_desc: "Whether you've never set foot in a gym or you're chasing Olympic dreams, we create a program uniquely yours.",
    complete_beginners: "Complete Beginners",
    beginners_desc: "Never exercised? No problem. We start where you are—even if that's from your couch.",
    youth_athletes: "Youth Athletes",
    youth_desc: "Age-appropriate training that builds foundations for lifelong athletic success.",
    active_adults: "Active Adults",
    adults_desc: "Take your fitness to the next level with periodized progressive training.",
    elite_athletes: "Elite Athletes",
    elite_desc: "Competition-level programming with precision periodization and peak performance focus.",
    you_tell_us: "You tell us who you are.",
    we_build: "We build exactly what you need.",
    
    // 100% Custom Section
    custom_title: "100% Custom. 100% Yours.",
    custom_desc: "Most fitness apps give everyone the same plan with minor tweaks. We don't. Every single program is generated from scratch based on your exact profile.",
    zero_templates: "Zero Templates. Zero Copies.",
    we_analyze: "We Analyze:",
    we_create: "We Create:",
    you_bring: "You bring the commitment.",
    we_handle: "We handle everything else.",
    data_points: "Data points analyzed per user",
    unique_combinations: "Unique program combinations",
    
    // Cultural Nutrition
    culturally_intelligent: "Culturally Intelligent",
    nutrition_respects: "Nutrition That Respects Your Culture",
    culture_desc: "Unlike generic meal plans built for Western palates, VYTA creates nutrition programs that celebrate your food heritage.",
    cuisines_supported: "cuisine traditions supported with authentic recipes",
    auto_detect: "Auto-detects your location for instant personalization",
    mix_cuisines: "Mix cuisines to match your multicultural taste",
    same_precision: "Same precision macros, familiar flavors you crave",
    
    // Features
    features_title: "Everything You Need",
    feature_training: "Personalized Training",
    feature_training_desc: "AI-powered programs adapted to your goals, equipment, and schedule.",
    feature_nutrition: "Custom Nutrition Plans",
    feature_nutrition_desc: "Meal plans tailored to your preferences, allergies, and cuisine.",
    feature_progress: "Progress Tracking",
    feature_progress_desc: "Visual insights and analytics to keep you on track.",
    feature_challenges: "Monthly Challenges",
    feature_challenges_desc: "Stay motivated with community-driven competitions.",
    
    // CTA
    cta_title: "Ready to Transform?",
    cta_subtitle: "Start your 7-day free trial today.",
    
    // Auth
    create_account: "Create Account",
    sign_in: "Sign In",
    email: "Email",
    password: "Password",
    confirm_password: "Confirm Password",
    name: "Full Name",
    already_have_account: "Already have an account?",
    dont_have_account: "Don't have an account?",
    
    // Common
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    next: "Next",
    back: "Back",
    submit: "Submit",
    continue: "Continue",
    
    // Onboarding
    onboarding_welcome: "Let's personalize your experience",
    select_country: "Select your country",
    date_of_birth: "Date of Birth",
    preferred_cuisines: "Preferred Cuisines",
    cuisine_hint: "Select cuisines you enjoy. This helps us create meal plans you'll love.",
    step: "Step",
    of: "of",
    
    // About Page
    our_story: "Our Story",
    making_health_accessible: "Making Health Accessible to All",
    about_intro: "We started VYTA with a simple belief: everyone deserves access to world-class fitness and nutrition guidance, regardless of where they live, what they can afford, or how they eat.",
    our_mission: "Our Mission",
    mission_text: "For too long, premium fitness coaching and personalized nutrition planning have been reserved for elite athletes and those who can afford personal trainers. We're changing that.",
    
    // Science Page
    evidence_based: "Evidence-Based Approach",
    science_title: "The Science Behind Your Transformation",
    science_subtitle: "Every recommendation we make is grounded in peer-reviewed research. Here's why our approach works—and the science that proves it.",
    cant_out_train: "You Can't Out-Train a Bad Diet",
    
    // Subscription
    subscription_title: "Start Your Trial",
    free_trial: "7-Day Free Trial",
    per_week: "/week",
    per_month: "/month",
    per_year: "/year",
    most_popular: "Most Popular",
    select_plan: "Select Plan",
    
    // Footer
    all_rights: "All rights reserved.",
    
    // Auth Page
    welcome_back: "Welcome back",
    create_your_account: "Create your account",
    log_in_continue: "Log in to continue your fitness journey",
    start_transformation: "Start your transformation with VYTA",
    full_name: "Full Name",
    enter_your_name: "Enter your name",
    passwords_not_match: "Passwords do not match",
    password_min_length: "Password must be at least 6 characters",
    back_to_home: "Back to home",
    sign_up: "Sign up",
    terms_agree: "By creating an account, you agree to our Terms of Service and Privacy Policy.",
    not_medical_advice: "This is not medical advice.",
    testimonial_quote: "VYTA transformed my approach to fitness. The AI programs are incredibly personalized.",
    
    // Dashboard
    welcome_back_name: "Welcome back",
    todays_workout: "Today's Workout",
    rest_day: "Rest Day",
    enjoy_rest_day: "Enjoy your rest day!",
    start_workout: "Start Workout",
    exercises: "exercises",
    current_streak: "Current Streak",
    days: "days",
    this_month: "This Month",
    workouts: "workouts",
    todays_nutrition: "Today's Nutrition",
    protein: "Protein",
    carbs: "Carbs",
    fat: "Fat",
    meals_planned: "meals planned",
    no_meal_plan: "No meal plan for today",
    weight: "Weight",
    log_weight: "Log Weight",
    active_challenges: "Active Challenges",
    active: "active",
    quick_actions: "Quick Actions",
    log_progress: "Log Progress",
    view_program: "View Program",
    contact: "Contact",
    settings_profile: "Settings & Profile",
    subscription: "Subscription",
    
    // Onboarding
    profile: "Profile",
    body_metrics: "Body Metrics",
    goals: "Goals",
    environment: "Environment",
    event_training: "Event Training",
    country: "Country",
    timezone: "Timezone",
    phone_optional: "Phone (optional)",
    height: "Height",
    weight_kg: "Weight",
    body_type: "Body Type",
    experience_level: "Experience Level",
    current_training_frequency: "Current Training Frequency",
    days_available: "Days Available to Train",
    time_per_session: "Time Per Session",
    injury_history: "Injury History (optional)",
    current_limitations: "Current Limitations (optional)",
    select_goals: "Select your goals and they'll be ranked by priority",
    custom_goal: "Custom Goal",
    where_train: "Where do you train?",
    gym_access: "Gym Access",
    home_no_equipment: "Home (No Equipment)",
    home_some_equipment: "Home (Some Equipment)",
    outdoors: "Outdoors",
    available_equipment: "Available Equipment",
    dietary_preference: "Dietary Preference",
    allergies: "Allergies",
    meals_per_day: "Meals per Day",
    cooking_time: "Cooking Time",
    budget: "Budget",
    foods_dislike: "Foods You Dislike",
    ingredients_exclude: "Ingredients to Exclude",
    complete_setup: "Complete Setup",
    saving_profile: "Saving Your Profile",
    almost_there: "Almost there! Next, start your free trial to access your programs...",
    
    // Subscription Page
    one_last_step: "One Last Step!",
    start_trial_access: "Start your 7-day free trial to access your personalized programs",
    trial_info: "Enter your card details to start. You won't be charged until after your trial ends. Cancel anytime during your trial to avoid any charges.",
    order_summary: "Order Summary",
    due_today: "due today",
    payment_details: "Payment Details",
    cardholder_name: "Cardholder Name",
    card_details: "Card Details",
    secured_by_stripe: "Secured by Stripe. Your payment info is encrypted.",
    trial_charge_notice: "By starting your trial, you agree to be charged",
    after: "after",
    unless_cancel: "days unless you cancel.",
    choose_different_plan: "Choose a different plan",
    full_program_access: "Full program access",
    personalized_nutrition: "Personalized nutrition",
    progress_tracking: "Progress tracking",
    monthly_challenges: "Monthly challenges",
    free_trial_active: "Free Trial Active",
    subscription_active: "Subscription Active",
    trial_ends: "Trial ends",
    cancel_subscription: "Cancel Subscription",
    reactivate_subscription: "Reactivate Subscription",
    cancels: "Cancels",
    days_left: "days left",
    secure_payment: "Secure payment powered by Stripe • Cancel anytime",
    
    // Profile Page
    profile_settings: "Profile & Settings",
    member_since: "Member since",
    edit: "Edit",
    training_preferences: "Training Preferences",
    nutrition_preferences: "Nutrition Preferences",
    days_week: "days/week",
    min_sessions: "min sessions",
    no_preference: "No preference",
    meals_day: "meals/day",
    days_per_week: "Days Per Week",
    session_duration: "Session Duration",
    training_location: "Training Location",
    generate_new_program: "Generate a new program after saving to apply these changes",
    save_training_preferences: "Save Training Preferences",
    save_nutrition_preferences: "Save Nutrition Preferences",
    generate_new_meal_plan: "Generate a new meal plan after saving to apply these changes",
    disliked_foods: "Disliked Foods",
    disliked_ingredients: "Disliked Ingredients",
    add_food_dislike: "Add a food you dislike",
    add_ingredient_exclude: "Add an ingredient to exclude",
    add: "Add",
    notifications: "Notifications",
    manage_notifications: "Manage your notification preferences",
    email_notifications: "Email Notifications",
    receive_email_updates: "Receive updates via email",
    sms_notifications: "SMS Notifications",
    receive_sms_updates: "Receive updates via SMS",
    inactivity_reminders: "Inactivity Reminders",
    inactivity_48h: "Get notified after 48 hours of inactivity",
    no_active_subscription: "No active subscription",
    plan: "plan",
    
    // About Page
    our_story: "Our Story",
    making_health_accessible: "Making Health Accessible to All",
    about_intro: "We started VYTA with a simple belief: everyone deserves access to world-class fitness and nutrition guidance, regardless of where they live, what they can afford, or how they eat.",
    our_mission: "Our Mission",
    mission_text: "For too long, premium fitness coaching and personalized nutrition planning have been reserved for elite athletes and those who can afford personal trainers. We're changing that.",
    health_first: "Health First",
    health_first_desc: "Every decision we make puts your wellbeing at the center.",
    globally_inclusive: "Globally Inclusive",
    globally_inclusive_desc: "Nutrition that respects and celebrates every culture.",
    community_driven: "Community Driven",
    community_driven_desc: "We grow together, learn together, succeed together.",
    science_backed: "Science-Backed",
    science_backed_desc: "Every recommendation grounded in peer-reviewed research.",
    problem_solving: "The Problem We're Solving",
    problem_solving_desc: "The fitness industry has failed most people. Here's why we're different.",
    generic_programs: "Generic Programs Don't Work",
    generic_programs_desc: "Cookie-cutter workout plans ignore your body type, schedule, equipment access, and limitations. We build every program specifically for you.",
    nutrition_ignores_culture: "Nutrition Plans Ignore Culture",
    nutrition_ignores_culture_desc: "Most diet plans are built for Western palates. If you're from Asia, Africa, South America, or anywhere else—your food traditions matter. We honor them.",
    quality_costs: "Quality Costs Too Much",
    quality_costs_desc: "Personal trainers charge $100-300/hour. Nutritionists cost thousands annually. We deliver the same personalization for less than the cost of a coffee per day.",
    what_we_stand_for: "What We Stand For",
    principles_guide: "The principles that guide everything we build",
    nutrition_speaks_language: "Nutrition That Speaks Your Language",
    join_movement: "Join the Movement",
    join_movement_desc: "Thousands of people across the globe are already transforming their lives with VYTA. Your journey to a healthier, happier you starts with a single step.",
    read_science: "Read the Science",
    
    // Science Page
    evidence_based: "Evidence-Based Approach",
    science_title: "The Science Behind Your Transformation",
    science_subtitle: "Every recommendation we make is grounded in peer-reviewed research. Here's why our approach works—and the science that proves it.",
    cant_out_train: "You Can't Out-Train a Bad Diet",
    cant_out_train_desc: "The fitness industry has long perpetuated a myth: work out hard enough, and you can eat whatever you want. The science tells a different story.",
    body_composition: "of body composition results come from nutrition, not exercise",
    calories_pound: "calories in 1 pound of fat—a 1-hour run burns only 600",
    no_weight_loss: "of people who exercise without dietary changes see no weight loss",
    research_clear: "The Research Is Clear",
    research_quote: "Exercise alone, without dietary intervention, results in only modest weight loss. The combination of diet and exercise produces superior outcomes across all health metrics.",
    research_source: "— American College of Sports Medicine Position Stand, 2023",
    research_guides: "Research That Guides Us",
    research_guides_desc: "Peer-reviewed studies from leading institutions form the foundation of our methodology",
    synergy_effect: "The Synergy Effect",
    synergy_desc: "When training and nutrition are perfectly aligned, the results multiply",
    training_stimulus: "Training Stimulus",
    training_stimulus_desc: "Exercise creates the stimulus for adaptation. It breaks down muscle fibers, depletes glycogen stores, and signals your body that it needs to grow stronger.",
    without_fuel: "Without proper fuel, this stimulus is wasted. Your body can't build what it doesn't have materials for.",
    nutritional_response: "Nutritional Response",
    nutritional_response_desc: "Nutrition provides the building blocks for recovery and growth. Protein repairs muscle, carbohydrates restore energy, and fats support hormonal function.",
    timing_nutrients: "Timing these nutrients around your training amplifies the adaptive response by up to 300%.",
    compounding_progress: "The Result: Compounding Progress",
    compounding_desc: "When training and nutrition work together, you don't just add results—you multiply them. Better recovery means harder training. Harder training means faster progress. This is how transformations happen.",
    apply_science: "How We Apply the Science",
    apply_science_desc: "The principles that power every VYTA nutrition plan",
    periodized_nutrition: "Periodized Nutrition",
    periodized_desc: "Your nutritional needs change based on training intensity, recovery demands, and goals. We adjust your macros and meal timing dynamically.",
    periodized_detail: "On training days, we increase carbohydrate intake to fuel performance. On rest days, we shift toward higher protein and fat for recovery and satiety.",
    nutrient_timing: "Nutrient Timing",
    nutrient_timing_desc: "When you eat matters almost as much as what you eat. We optimize meal timing around your workout schedule.",
    nutrient_timing_detail: "Pre-workout meals are designed for sustained energy. Post-workout nutrition prioritizes protein synthesis and glycogen replenishment.",
    micronutrient_optimization: "Micronutrient Optimization",
    micronutrient_desc: "Beyond macros, we ensure your meals provide essential vitamins, minerals, and phytonutrients for optimal health.",
    micronutrient_detail: "Every meal plan is analyzed for iron, zinc, vitamin D, omega-3s, and other critical nutrients often lacking in modern diets.",
    gut_health: "Gut Health Integration",
    gut_health_desc: "A healthy gut is foundational to nutrient absorption, immune function, and even mental health.",
    gut_health_detail: "We incorporate prebiotic and probiotic-rich foods naturally into your meal plans to support your microbiome.",
    science_backed_personal: "Science-Backed. Personally Delivered.",
    stop_guessing: "Stop guessing. Start progressing. Let research-driven AI create your personalized path to results.",
    start_science_journey: "Start Your Science-Backed Journey",
    
    // Common UI elements
    minutes: "minutes",
    meals: "meals",
    select: "Select",
    gym: "Gym",
    home: "Home",
    privacy: "Privacy",
    terms: "Terms",
    
    // Training Page
    training_program: "Training Program",
    personalized: "Personalized",
    duration: "Duration",
    weeks: "weeks",
    current_week: "Current Week",
    week: "Week",
    sessions: "Sessions",
    per_week: "per week",
    weekly_schedule: "Weekly Schedule",
    today: "Today",
    exercises_count: "exercises",
    sets: "sets",
    reps: "reps",
    rest: "Rest",
    sec: "sec",
    coaching_tips: "Coaching Tips",
    no_program: "No training program available",
    generate_program: "Generate Program",
    regenerate_program: "Regenerate Program",
    
    // Nutrition Page
    nutrition_plan: "Nutrition Plan",
    meal_plan: "Meal Plan",
    daily_nutrition: "Daily Nutrition",
    calories: "Calories",
    kcal: "kcal",
    macros: "Macros",
    shopping_list: "Shopping List",
    download_pdf: "Download PDF",
    ingredients: "Ingredients",
    instructions: "Instructions",
    prep_time: "Prep Time",
    cook_time: "Cook Time",
    servings: "Servings",
    no_meal_plan_available: "No meal plan available",
    generate_meal_plan: "Generate Meal Plan",
    
    // Progress Page
    progress_tracking: "Progress Tracking",
    log_new_entry: "Log New Entry",
    select_metric: "Select Metric",
    enter_value: "Enter Value",
    notes_optional: "Notes (optional)",
    add_entry: "Add Entry",
    no_progress_data: "No progress data yet",
    start_logging: "Start logging your progress to see trends",
    total_entries: "Total Entries",
    last_entry: "Last Entry",
    change: "Change",
    measurement: "Measurement",
    performance: "Performance",
  },
  
  zh: {
    // Landing Page
    hero_title: "您的计划。为您而建。独一无二。",
    hero_subtitle: "精英级表现计划,为您量身定制。以科学为基础。",
    hero_desc: "每个计划都是100%独特的——根据您的身体、目标、时间和生活量身打造。我们创建,您来征服。",
    get_started: "开始使用",
    login: "登录",
    start_free_trial: "开始免费试用",
    watch_video: "观看视频",
    
    // Navigation
    about: "关于我们",
    science: "科学依据",
    dashboard: "仪表板",
    training: "训练",
    nutrition: "营养",
    progress: "进度",
    challenges: "挑战",
    messages: "消息",
    profile: "个人资料",
    logout: "退出登录",
    
    // For Everyone Section
    built_for_everyone: "为每个人而建",
    everyone_desc: "无论您从未踏入健身房还是追逐奥运梦想,我们都会为您创建独特的计划。",
    complete_beginners: "完全初学者",
    beginners_desc: "从未锻炼过?没问题。我们从您所在的位置开始——即使是从沙发上。",
    youth_athletes: "青年运动员",
    youth_desc: "适合年龄的训练,为终身运动成功奠定基础。",
    active_adults: "活跃成年人",
    adults_desc: "通过周期化渐进训练将您的健身提升到新水平。",
    elite_athletes: "精英运动员",
    elite_desc: "竞技级别的编程,精确周期化和巅峰表现聚焦。",
    you_tell_us: "告诉我们您是谁。",
    we_build: "我们为您量身打造所需。",
    
    // 100% Custom Section
    custom_title: "100%定制。100%属于您。",
    custom_desc: "大多数健身应用给每个人相同的计划,只有细微调整。我们不是。每个计划都是根据您的确切资料从头生成的。",
    zero_templates: "零模板。零复制。",
    we_analyze: "我们分析:",
    we_create: "我们创建:",
    you_bring: "您带来承诺。",
    we_handle: "我们处理其他一切。",
    data_points: "每位用户分析的数据点",
    unique_combinations: "独特的计划组合",
    
    // Cultural Nutrition
    culturally_intelligent: "文化智能",
    nutrition_respects: "尊重您文化的营养",
    culture_desc: "与为西方口味设计的通用餐计划不同,VYTA创建庆祝您饮食传统的营养计划。",
    cuisines_supported: "种美食传统配有正宗食谱",
    auto_detect: "自动检测您的位置以实现即时个性化",
    mix_cuisines: "混合美食以匹配您的多元文化口味",
    same_precision: "相同精确的宏量营养素,您渴望的熟悉味道",
    
    // Features
    features_title: "您需要的一切",
    feature_training: "个性化训练",
    feature_training_desc: "基于AI的计划,适应您的目标、设备和时间表。",
    feature_nutrition: "定制营养计划",
    feature_nutrition_desc: "根据您的偏好、过敏原和美食风格定制的膳食计划。",
    feature_progress: "进度追踪",
    feature_progress_desc: "可视化分析,让您保持正轨。",
    feature_challenges: "月度挑战",
    feature_challenges_desc: "通过社区驱动的竞赛保持动力。",
    
    // CTA
    cta_title: "准备好改变了吗?",
    cta_subtitle: "今天开始您的7天免费试用。",
    
    // Auth
    create_account: "创建账户",
    sign_in: "登录",
    email: "电子邮件",
    password: "密码",
    confirm_password: "确认密码",
    name: "全名",
    already_have_account: "已有账户?",
    dont_have_account: "没有账户?",
    
    // Common
    loading: "加载中...",
    save: "保存",
    cancel: "取消",
    next: "下一步",
    back: "返回",
    submit: "提交",
    continue: "继续",
    
    // Onboarding
    onboarding_welcome: "让我们个性化您的体验",
    select_country: "选择您的国家",
    date_of_birth: "出生日期",
    preferred_cuisines: "偏好美食",
    cuisine_hint: "选择您喜欢的美食风格。这有助于我们创建您喜爱的膳食计划。",
    step: "步骤",
    of: "/",
    
    // About Page
    our_story: "我们的故事",
    making_health_accessible: "让健康触手可及",
    about_intro: "我们创立VYTA是基于一个简单的信念:每个人都应该获得世界级的健身和营养指导,无论他们住在哪里,能负担多少,或如何饮食。",
    our_mission: "我们的使命",
    mission_text: "长期以来,高端健身教练和个性化营养规划一直是精英运动员和能负担得起私人教练的人的专利。我们正在改变这一切。",
    
    // Science Page
    evidence_based: "循证方法",
    science_title: "您转变背后的科学",
    science_subtitle: "我们提出的每一条建议都以同行评审的研究为基础。这就是我们的方法有效的原因——以及证明它的科学。",
    cant_out_train: "训练无法弥补糟糕的饮食",
    
    // Subscription
    subscription_title: "开始试用",
    free_trial: "7天免费试用",
    per_week: "/周",
    per_month: "/月",
    per_year: "/年",
    most_popular: "最受欢迎",
    select_plan: "选择计划",
    
    // Footer
    all_rights: "保留所有权利。",
    
    // Auth Page
    welcome_back: "欢迎回来",
    create_your_account: "创建您的账户",
    log_in_continue: "登录以继续您的健身之旅",
    start_transformation: "与VYTA开始您的转变",
    full_name: "全名",
    enter_your_name: "输入您的姓名",
    passwords_not_match: "密码不匹配",
    password_min_length: "密码至少6个字符",
    back_to_home: "返回首页",
    sign_up: "注册",
    terms_agree: "创建账户即表示您同意我们的服务条款和隐私政策。",
    not_medical_advice: "这不是医疗建议。",
    testimonial_quote: "VYTA改变了我对健身的方法。AI程序非常个性化。",
    
    // Dashboard
    welcome_back_name: "欢迎回来",
    todays_workout: "今日训练",
    rest_day: "休息日",
    enjoy_rest_day: "享受您的休息日！",
    start_workout: "开始训练",
    exercises: "个动作",
    current_streak: "当前连续",
    days: "天",
    this_month: "本月",
    workouts: "次训练",
    todays_nutrition: "今日营养",
    protein: "蛋白质",
    carbs: "碳水化合物",
    fat: "脂肪",
    meals_planned: "餐已计划",
    weight: "体重",
    log_weight: "记录体重",
    active_challenges: "进行中的挑战",
    active: "进行中",
    quick_actions: "快速操作",
    log_progress: "记录进度",
    view_program: "查看计划",
    contact: "联系",
    settings_profile: "设置与个人资料",
    subscription: "订阅",
    
    // Onboarding
    profile: "个人资料",
    body_metrics: "身体数据",
    goals: "目标",
    environment: "环境",
    country: "国家",
    timezone: "时区",
    phone_optional: "电话（可选）",
    height: "身高",
    weight_kg: "体重",
    body_type: "体型",
    experience_level: "经验水平",
    current_training_frequency: "当前训练频率",
    days_available: "可训练天数",
    time_per_session: "每次训练时间",
    injury_history: "伤病史（可选）",
    current_limitations: "当前限制（可选）",
    select_goals: "选择您的目标，它们将按优先级排序",
    custom_goal: "自定义目标",
    where_train: "您在哪里训练？",
    gym_access: "健身房",
    home_no_equipment: "家（无器械）",
    home_some_equipment: "家（有器械）",
    outdoors: "户外",
    available_equipment: "可用器械",
    dietary_preference: "饮食偏好",
    allergies: "过敏原",
    meals_per_day: "每日餐数",
    cooking_time: "烹饪时间",
    budget: "预算",
    foods_dislike: "不喜欢的食物",
    ingredients_exclude: "要排除的食材",
    complete_setup: "完成设置",
    saving_profile: "保存您的资料",
    almost_there: "快完成了！接下来，开始免费试用以访问您的计划...",
    
    // Subscription Page
    one_last_step: "最后一步！",
    start_trial_access: "开始7天免费试用以访问您的个性化计划",
    trial_info: "输入您的卡信息开始。试用结束前不会收费。试用期间随时取消以避免收费。",
    order_summary: "订单摘要",
    due_today: "今日应付",
    payment_details: "付款详情",
    cardholder_name: "持卡人姓名",
    card_details: "卡片详情",
    secured_by_stripe: "由Stripe安全保障。您的付款信息已加密。",
    trial_charge_notice: "开始试用即表示您同意在",
    after: "后收取",
    unless_cancel: "天，除非您取消。",
    choose_different_plan: "选择其他计划",
    full_program_access: "完整计划访问",
    personalized_nutrition: "个性化营养",
    progress_tracking: "进度追踪",
    monthly_challenges: "月度挑战",
    free_trial_active: "免费试用进行中",
    subscription_active: "订阅已激活",
    trial_ends: "试用结束",
    cancel_subscription: "取消订阅",
    reactivate_subscription: "重新激活订阅",
    cancels: "取消于",
    days_left: "天剩余",
    secure_payment: "由Stripe提供安全支付 • 随时取消",
    
    // Profile Page
    profile_settings: "个人资料与设置",
    member_since: "注册于",
    edit: "编辑",
    training_preferences: "训练偏好",
    nutrition_preferences: "营养偏好",
    days_week: "天/周",
    min_sessions: "分钟/次",
    no_preference: "无偏好",
    meals_day: "餐/天",
    days_per_week: "每周天数",
    session_duration: "训练时长",
    training_location: "训练地点",
    generate_new_program: "保存后生成新计划以应用这些更改",
    save_training_preferences: "保存训练偏好",
    save_nutrition_preferences: "保存营养偏好",
    generate_new_meal_plan: "保存后生成新餐计划以应用这些更改",
    disliked_foods: "不喜欢的食物",
    disliked_ingredients: "不喜欢的食材",
    add_food_dislike: "添加不喜欢的食物",
    add_ingredient_exclude: "添加要排除的食材",
    add: "添加",
    notifications: "通知",
    manage_notifications: "管理您的通知偏好",
    email_notifications: "邮件通知",
    receive_email_updates: "通过邮件接收更新",
    sms_notifications: "短信通知",
    receive_sms_updates: "通过短信接收更新",
    inactivity_reminders: "不活动提醒",
    inactivity_48h: "48小时无活动后通知",
    no_active_subscription: "无活跃订阅",
    plan: "计划",
    
    // About Page
    our_story: "我们的故事",
    making_health_accessible: "让健康触手可及",
    about_intro: "我们创立VYTA是基于一个简单的信念：每个人都应该获得世界级的健身和营养指导，无论他们住在哪里、能负担多少或如何饮食。",
    our_mission: "我们的使命",
    mission_text: "长期以来，高端健身教练和个性化营养规划一直是精英运动员和能负担得起私人教练的人的专利。我们正在改变这一切。",
    health_first: "健康优先",
    health_first_desc: "我们做的每一个决定都以您的健康为中心。",
    globally_inclusive: "全球包容",
    globally_inclusive_desc: "尊重并庆祝每种文化的营养。",
    community_driven: "社区驱动",
    community_driven_desc: "我们一起成长，一起学习，一起成功。",
    science_backed: "科学支持",
    science_backed_desc: "每条建议都基于同行评审的研究。",
    problem_solving: "我们正在解决的问题",
    problem_solving_desc: "健身行业让大多数人失望了。这就是我们不同的原因。",
    generic_programs: "通用计划不起作用",
    generic_programs_desc: "千篇一律的锻炼计划忽略了您的体型、时间表、器械和限制。我们专门为您制定每个计划。",
    nutrition_ignores_culture: "营养计划忽视文化",
    nutrition_ignores_culture_desc: "大多数饮食计划是为西方口味设计的。如果您来自亚洲、非洲、南美或其他地方——您的食物传统很重要。我们尊重它们。",
    quality_costs: "优质成本太高",
    quality_costs_desc: "私人教练每小时收费100-300美元。营养师每年花费数千。我们以不到一杯咖啡的价格提供同样的个性化服务。",
    what_we_stand_for: "我们代表什么",
    principles_guide: "指导我们建设一切的原则",
    nutrition_speaks_language: "说您语言的营养",
    join_movement: "加入运动",
    join_movement_desc: "全球已有成千上万的人正在用VYTA改变他们的生活。您通往更健康、更快乐的旅程从一步开始。",
    read_science: "阅读科学",
    
    // Science Page
    evidence_based: "循证方法",
    science_title: "您转变背后的科学",
    science_subtitle: "我们提出的每一条建议都以同行评审的研究为基础。这就是我们的方法有效的原因——以及证明它的科学。",
    cant_out_train: "训练无法弥补糟糕的饮食",
    cant_out_train_desc: "健身行业长期以来一直在传播一个神话：只要努力锻炼，就可以吃任何东西。科学告诉我们不同的故事。",
    body_composition: "的身体组成结果来自营养，而不是运动",
    calories_pound: "卡路里在1磅脂肪中——1小时跑步只燃烧600",
    no_weight_loss: "不改变饮食就运动的人没有减重",
    research_clear: "研究很清楚",
    research_quote: "单独运动，不进行饮食干预，只会导致适度的体重减轻。饮食和运动的结合在所有健康指标上产生更好的结果。",
    research_source: "— 美国运动医学学院立场声明，2023",
    research_guides: "指导我们的研究",
    research_guides_desc: "来自领先机构的同行评审研究构成了我们方法论的基础",
    synergy_effect: "协同效应",
    synergy_desc: "当训练和营养完美结合时，结果会成倍增加",
    training_stimulus: "训练刺激",
    training_stimulus_desc: "运动创造适应的刺激。它分解肌肉纤维，耗尽糖原储存，并向身体发出需要变强的信号。",
    without_fuel: "没有适当的燃料，这种刺激就被浪费了。您的身体无法建造没有材料的东西。",
    nutritional_response: "营养响应",
    nutritional_response_desc: "营养提供恢复和生长的基础。蛋白质修复肌肉，碳水化合物恢复能量，脂肪支持激素功能。",
    timing_nutrients: "围绕训练安排这些营养素的时间可以将适应性反应放大300%。",
    compounding_progress: "结果：复合进步",
    compounding_desc: "当训练和营养一起工作时，您不只是增加结果——而是成倍增加。更好的恢复意味着更努力的训练。更努力的训练意味着更快的进步。这就是转变发生的方式。",
    apply_science: "我们如何应用科学",
    apply_science_desc: "驱动每个VYTA营养计划的原则",
    periodized_nutrition: "周期化营养",
    periodized_desc: "您的营养需求会根据训练强度、恢复需求和目标而变化。我们动态调整您的宏量营养素和用餐时间。",
    periodized_detail: "在训练日，我们增加碳水化合物摄入以提供能量。在休息日，我们转向更高的蛋白质和脂肪以促进恢复和饱腹感。",
    nutrient_timing: "营养时机",
    nutrient_timing_desc: "什么时候吃几乎和吃什么一样重要。我们围绕您的锻炼时间表优化用餐时间。",
    nutrient_timing_detail: "锻炼前的餐食设计用于持续能量。锻炼后的营养优先考虑蛋白质合成和糖原补充。",
    micronutrient_optimization: "微量营养素优化",
    micronutrient_desc: "除了宏量营养素，我们确保您的餐食提供必需的维生素、矿物质和植物营养素以获得最佳健康。",
    micronutrient_detail: "每个餐计划都会分析铁、锌、维生素D、欧米伽-3和现代饮食中经常缺乏的其他关键营养素。",
    gut_health: "肠道健康整合",
    gut_health_desc: "健康的肠道是营养吸收、免疫功能甚至心理健康的基础。",
    gut_health_detail: "我们自然地将益生元和益生菌丰富的食物纳入您的餐计划以支持您的微生物群。",
    science_backed_personal: "科学支持。个人递送。",
    stop_guessing: "停止猜测。开始进步。让研究驱动的AI为您创建个性化的结果之路。",
    start_science_journey: "开始您的科学之旅",
    
    // Common UI elements
    minutes: "分钟",
    meals: "餐",
    select: "选择",
    gym: "健身房",
    home: "家",
    privacy: "隐私",
    terms: "条款",
  },
  
  es: {
    // Landing Page
    hero_title: "Tu Plan. Hecho para Ti. Solo para Ti.",
    hero_subtitle: "Programas de rendimiento elite, personalizados para ti. Basados en ciencia.",
    hero_desc: "Cada programa es 100% único—construido a medida para tu cuerpo, tus objetivos, tu horario y tu vida. Nosotros lo creamos. Tú lo conquistas.",
    get_started: "Comenzar",
    login: "Iniciar Sesión",
    start_free_trial: "Comenzar Prueba Gratis",
    watch_video: "Ver Video",
    
    // Navigation
    about: "Nosotros",
    science: "Ciencia",
    dashboard: "Panel",
    training: "Entrenamiento",
    nutrition: "Nutrición",
    progress: "Progreso",
    challenges: "Desafíos",
    messages: "Mensajes",
    profile: "Perfil",
    logout: "Cerrar Sesión",
    
    // For Everyone Section
    built_for_everyone: "Hecho para Todos",
    everyone_desc: "Ya sea que nunca hayas pisado un gimnasio o estés persiguiendo sueños olímpicos, creamos un programa único para ti.",
    complete_beginners: "Principiantes",
    beginners_desc: "¿Nunca has hecho ejercicio? No hay problema. Empezamos donde estás—incluso desde tu sofá.",
    youth_athletes: "Atletas Jóvenes",
    youth_desc: "Entrenamiento apropiado para la edad que construye bases para el éxito atlético de por vida.",
    active_adults: "Adultos Activos",
    adults_desc: "Lleva tu fitness al siguiente nivel con entrenamiento progresivo periodizado.",
    elite_athletes: "Atletas Elite",
    elite_desc: "Programación nivel competición con periodización precisa y enfoque en rendimiento máximo.",
    you_tell_us: "Nos dices quién eres.",
    we_build: "Construimos exactamente lo que necesitas.",
    
    // 100% Custom Section
    custom_title: "100% Personalizado. 100% Tuyo.",
    custom_desc: "La mayoría de apps dan a todos el mismo plan con ajustes menores. Nosotros no. Cada programa se genera desde cero basado en tu perfil exacto.",
    zero_templates: "Cero Plantillas. Cero Copias.",
    we_analyze: "Analizamos:",
    we_create: "Creamos:",
    you_bring: "Tú traes el compromiso.",
    we_handle: "Nosotros manejamos todo lo demás.",
    data_points: "Puntos de datos analizados por usuario",
    unique_combinations: "Combinaciones únicas de programas",
    
    // Cultural Nutrition
    culturally_intelligent: "Culturalmente Inteligente",
    nutrition_respects: "Nutrición que Respeta tu Cultura",
    culture_desc: "A diferencia de planes genéricos para paladares occidentales, VYTA crea programas de nutrición que celebran tu herencia alimentaria.",
    cuisines_supported: "tradiciones culinarias con recetas auténticas",
    auto_detect: "Detecta automáticamente tu ubicación para personalización instantánea",
    mix_cuisines: "Mezcla cocinas para tu gusto multicultural",
    same_precision: "Misma precisión en macros, sabores familiares que anhelas",
    
    // Features
    features_title: "Todo lo que Necesitas",
    feature_training: "Entrenamiento Personalizado",
    feature_training_desc: "Programas impulsados por IA adaptados a tus objetivos, equipo y horario.",
    feature_nutrition: "Planes de Nutrición Personalizados",
    feature_nutrition_desc: "Planes de comidas adaptados a tus preferencias, alergias y cocina.",
    feature_progress: "Seguimiento de Progreso",
    feature_progress_desc: "Información visual y análisis para mantenerte en el camino.",
    feature_challenges: "Desafíos Mensuales",
    feature_challenges_desc: "Mantente motivado con competencias de la comunidad.",
    
    // CTA
    cta_title: "¿Listo para Transformarte?",
    cta_subtitle: "Comienza tu prueba gratuita de 7 días hoy.",
    
    // Auth
    create_account: "Crear Cuenta",
    sign_in: "Iniciar Sesión",
    email: "Correo Electrónico",
    password: "Contraseña",
    confirm_password: "Confirmar Contraseña",
    name: "Nombre Completo",
    already_have_account: "¿Ya tienes una cuenta?",
    dont_have_account: "¿No tienes una cuenta?",
    
    // Common
    loading: "Cargando...",
    save: "Guardar",
    cancel: "Cancelar",
    next: "Siguiente",
    back: "Atrás",
    submit: "Enviar",
    continue: "Continuar",
    
    // Onboarding
    onboarding_welcome: "Personalicemos tu experiencia",
    select_country: "Selecciona tu país",
    date_of_birth: "Fecha de Nacimiento",
    preferred_cuisines: "Cocinas Preferidas",
    cuisine_hint: "Selecciona las cocinas que disfrutas. Esto nos ayuda a crear planes de comidas que te encantarán.",
    step: "Paso",
    of: "de",
    
    // About Page
    our_story: "Nuestra Historia",
    making_health_accessible: "Haciendo la Salud Accesible para Todos",
    about_intro: "Comenzamos VYTA con una creencia simple: todos merecen acceso a guía de fitness y nutrición de clase mundial, sin importar dónde vivan, qué puedan pagar o cómo coman.",
    our_mission: "Nuestra Misión",
    mission_text: "Durante demasiado tiempo, el coaching de fitness premium y la planificación nutricional personalizada han sido reservados para atletas de élite y quienes pueden pagar entrenadores personales. Estamos cambiando eso.",
    
    // Science Page
    evidence_based: "Enfoque Basado en Evidencia",
    science_title: "La Ciencia Detrás de tu Transformación",
    science_subtitle: "Cada recomendación que hacemos está fundamentada en investigación revisada por pares. Aquí está por qué funciona nuestro enfoque—y la ciencia que lo prueba.",
    cant_out_train: "No Puedes Superar una Mala Dieta con Ejercicio",
    
    // Subscription
    subscription_title: "Comienza tu Prueba",
    free_trial: "Prueba Gratis de 7 Días",
    per_week: "/semana",
    per_month: "/mes",
    per_year: "/año",
    most_popular: "Más Popular",
    select_plan: "Seleccionar Plan",
    
    // Footer
    all_rights: "Todos los derechos reservados.",
    
    // Auth Page
    welcome_back: "Bienvenido de nuevo",
    create_your_account: "Crea tu cuenta",
    log_in_continue: "Inicia sesión para continuar tu viaje fitness",
    start_transformation: "Comienza tu transformación con VYTA",
    full_name: "Nombre Completo",
    enter_your_name: "Ingresa tu nombre",
    passwords_not_match: "Las contraseñas no coinciden",
    password_min_length: "La contraseña debe tener al menos 6 caracteres",
    back_to_home: "Volver al inicio",
    sign_up: "Registrarse",
    terms_agree: "Al crear una cuenta, aceptas nuestros Términos de Servicio y Política de Privacidad.",
    not_medical_advice: "Esto no es consejo médico.",
    testimonial_quote: "VYTA transformó mi enfoque del fitness. Los programas de IA son increíblemente personalizados.",
    
    // Dashboard
    welcome_back_name: "Bienvenido de nuevo",
    todays_workout: "Entrenamiento de Hoy",
    rest_day: "Día de Descanso",
    enjoy_rest_day: "¡Disfruta tu día de descanso!",
    start_workout: "Iniciar Entrenamiento",
    exercises: "ejercicios",
    current_streak: "Racha Actual",
    days: "días",
    this_month: "Este Mes",
    workouts: "entrenamientos",
    todays_nutrition: "Nutrición de Hoy",
    protein: "Proteína",
    carbs: "Carbohidratos",
    fat: "Grasa",
    meals_planned: "comidas planificadas",
    weight: "Peso",
    log_weight: "Registrar Peso",
    active_challenges: "Desafíos Activos",
    active: "activos",
    quick_actions: "Acciones Rápidas",
    log_progress: "Registrar Progreso",
    view_program: "Ver Programa",
    contact: "Contacto",
    settings_profile: "Configuración y Perfil",
    subscription: "Suscripción",
    
    // Onboarding
    profile: "Perfil",
    body_metrics: "Métricas Corporales",
    goals: "Objetivos",
    environment: "Entorno",
    country: "País",
    timezone: "Zona Horaria",
    phone_optional: "Teléfono (opcional)",
    height: "Altura",
    weight_kg: "Peso",
    body_type: "Tipo de Cuerpo",
    experience_level: "Nivel de Experiencia",
    current_training_frequency: "Frecuencia de Entrenamiento Actual",
    days_available: "Días Disponibles para Entrenar",
    time_per_session: "Tiempo por Sesión",
    injury_history: "Historial de Lesiones (opcional)",
    current_limitations: "Limitaciones Actuales (opcional)",
    select_goals: "Selecciona tus objetivos y se ordenarán por prioridad",
    custom_goal: "Objetivo Personalizado",
    where_train: "¿Dónde entrenas?",
    gym_access: "Acceso al Gimnasio",
    home_no_equipment: "Casa (Sin Equipo)",
    home_some_equipment: "Casa (Algo de Equipo)",
    outdoors: "Al Aire Libre",
    available_equipment: "Equipo Disponible",
    dietary_preference: "Preferencia Dietética",
    allergies: "Alergias",
    meals_per_day: "Comidas por Día",
    cooking_time: "Tiempo de Cocina",
    budget: "Presupuesto",
    foods_dislike: "Alimentos que No Te Gustan",
    ingredients_exclude: "Ingredientes a Excluir",
    complete_setup: "Completar Configuración",
    saving_profile: "Guardando Tu Perfil",
    almost_there: "¡Casi listo! A continuación, inicia tu prueba gratuita para acceder a tus programas...",
    
    // Subscription Page
    one_last_step: "¡Un Último Paso!",
    start_trial_access: "Inicia tu prueba gratuita de 7 días para acceder a tus programas personalizados",
    trial_info: "Ingresa los datos de tu tarjeta para comenzar. No se te cobrará hasta que termine tu prueba. Cancela en cualquier momento durante tu prueba para evitar cargos.",
    order_summary: "Resumen del Pedido",
    due_today: "a pagar hoy",
    payment_details: "Detalles de Pago",
    cardholder_name: "Nombre del Titular",
    card_details: "Datos de la Tarjeta",
    secured_by_stripe: "Asegurado por Stripe. Tu información de pago está encriptada.",
    trial_charge_notice: "Al iniciar tu prueba, aceptas que se te cobre",
    after: "después de",
    unless_cancel: "días a menos que canceles.",
    choose_different_plan: "Elegir otro plan",
    full_program_access: "Acceso completo al programa",
    personalized_nutrition: "Nutrición personalizada",
    progress_tracking: "Seguimiento de progreso",
    monthly_challenges: "Desafíos mensuales",
    free_trial_active: "Prueba Gratuita Activa",
    subscription_active: "Suscripción Activa",
    trial_ends: "La prueba termina",
    cancel_subscription: "Cancelar Suscripción",
    reactivate_subscription: "Reactivar Suscripción",
    cancels: "Se cancela",
    days_left: "días restantes",
    secure_payment: "Pago seguro con Stripe • Cancela cuando quieras",
    
    // Profile Page
    profile_settings: "Perfil y Configuración",
    member_since: "Miembro desde",
    edit: "Editar",
    training_preferences: "Preferencias de Entrenamiento",
    nutrition_preferences: "Preferencias de Nutrición",
    days_week: "días/semana",
    min_sessions: "min/sesión",
    no_preference: "Sin preferencia",
    meals_day: "comidas/día",
    days_per_week: "Días por Semana",
    session_duration: "Duración de Sesión",
    training_location: "Lugar de Entrenamiento",
    generate_new_program: "Genera un nuevo programa después de guardar para aplicar estos cambios",
    save_training_preferences: "Guardar Preferencias de Entrenamiento",
    save_nutrition_preferences: "Guardar Preferencias de Nutrición",
    generate_new_meal_plan: "Genera un nuevo plan de comidas después de guardar para aplicar estos cambios",
    disliked_foods: "Alimentos que No Te Gustan",
    disliked_ingredients: "Ingredientes que No Te Gustan",
    add_food_dislike: "Añade un alimento que no te guste",
    add_ingredient_exclude: "Añade un ingrediente a excluir",
    add: "Añadir",
    notifications: "Notificaciones",
    manage_notifications: "Gestiona tus preferencias de notificación",
    email_notifications: "Notificaciones por Email",
    receive_email_updates: "Recibe actualizaciones por email",
    sms_notifications: "Notificaciones SMS",
    receive_sms_updates: "Recibe actualizaciones por SMS",
    inactivity_reminders: "Recordatorios de Inactividad",
    inactivity_48h: "Recibe notificación después de 48 horas de inactividad",
    no_active_subscription: "Sin suscripción activa",
    plan: "plan",
    
    // About Page
    our_story: "Nuestra Historia",
    making_health_accessible: "Haciendo la Salud Accesible para Todos",
    about_intro: "Comenzamos VYTA con una creencia simple: todos merecen acceso a guía de fitness y nutrición de clase mundial, sin importar dónde vivan, qué puedan pagar o cómo coman.",
    our_mission: "Nuestra Misión",
    mission_text: "Durante demasiado tiempo, el coaching de fitness premium y la planificación nutricional personalizada han sido reservados para atletas de élite y quienes pueden pagar entrenadores personales. Estamos cambiando eso.",
    health_first: "Salud Primero",
    health_first_desc: "Cada decisión que tomamos pone tu bienestar en el centro.",
    globally_inclusive: "Globalmente Inclusivo",
    globally_inclusive_desc: "Nutrición que respeta y celebra cada cultura.",
    community_driven: "Impulsado por la Comunidad",
    community_driven_desc: "Crecemos juntos, aprendemos juntos, tenemos éxito juntos.",
    science_backed: "Respaldado por la Ciencia",
    science_backed_desc: "Cada recomendación fundamentada en investigación revisada por pares.",
    problem_solving: "El Problema que Resolvemos",
    problem_solving_desc: "La industria del fitness ha fallado a la mayoría de las personas. Aquí está por qué somos diferentes.",
    generic_programs: "Los Programas Genéricos No Funcionan",
    generic_programs_desc: "Los planes de entrenamiento genéricos ignoran tu tipo de cuerpo, horario, acceso a equipo y limitaciones. Construimos cada programa específicamente para ti.",
    nutrition_ignores_culture: "Los Planes de Nutrición Ignoran la Cultura",
    nutrition_ignores_culture_desc: "La mayoría de los planes de dieta están hechos para paladares occidentales. Si eres de Asia, África, Sudamérica o cualquier otro lugar, tus tradiciones alimenticias importan. Las honramos.",
    quality_costs: "La Calidad Cuesta Demasiado",
    quality_costs_desc: "Los entrenadores personales cobran $100-300/hora. Los nutricionistas cuestan miles al año. Ofrecemos la misma personalización por menos del costo de un café al día.",
    what_we_stand_for: "Lo Que Defendemos",
    principles_guide: "Los principios que guían todo lo que construimos",
    nutrition_speaks_language: "Nutrición que Habla Tu Idioma",
    join_movement: "Únete al Movimiento",
    join_movement_desc: "Miles de personas en todo el mundo ya están transformando sus vidas con VYTA. Tu viaje hacia una vida más saludable y feliz comienza con un solo paso.",
    read_science: "Lee la Ciencia",
    
    // Science Page
    evidence_based: "Enfoque Basado en Evidencia",
    science_title: "La Ciencia Detrás de Tu Transformación",
    science_subtitle: "Cada recomendación que hacemos está fundamentada en investigación revisada por pares. Aquí está por qué funciona nuestro enfoque—y la ciencia que lo prueba.",
    cant_out_train: "No Puedes Superar una Mala Dieta con Ejercicio",
    cant_out_train_desc: "La industria del fitness ha perpetuado un mito: entrena lo suficientemente duro y podrás comer lo que quieras. La ciencia cuenta una historia diferente.",
    body_composition: "de los resultados de composición corporal provienen de la nutrición, no del ejercicio",
    calories_pound: "calorías en medio kilo de grasa—una hora corriendo solo quema 600",
    no_weight_loss: "de las personas que hacen ejercicio sin cambios en la dieta no ven pérdida de peso",
    research_clear: "La Investigación Es Clara",
    research_quote: "El ejercicio solo, sin intervención dietética, resulta en una pérdida de peso modesta. La combinación de dieta y ejercicio produce resultados superiores en todas las métricas de salud.",
    research_source: "— Declaración de Posición del Colegio Americano de Medicina Deportiva, 2023",
    research_guides: "Investigación que Nos Guía",
    research_guides_desc: "Estudios revisados por pares de instituciones líderes forman la base de nuestra metodología",
    synergy_effect: "El Efecto de Sinergia",
    synergy_desc: "Cuando el entrenamiento y la nutrición están perfectamente alineados, los resultados se multiplican",
    training_stimulus: "Estímulo de Entrenamiento",
    training_stimulus_desc: "El ejercicio crea el estímulo para la adaptación. Descompone las fibras musculares, agota las reservas de glucógeno y señala a tu cuerpo que necesita fortalecerse.",
    without_fuel: "Sin el combustible adecuado, este estímulo se desperdicia. Tu cuerpo no puede construir lo que no tiene materiales.",
    nutritional_response: "Respuesta Nutricional",
    nutritional_response_desc: "La nutrición proporciona los bloques de construcción para la recuperación y el crecimiento. La proteína repara el músculo, los carbohidratos restauran la energía y las grasas apoyan la función hormonal.",
    timing_nutrients: "Sincronizar estos nutrientes alrededor de tu entrenamiento amplifica la respuesta adaptativa hasta en un 300%.",
    compounding_progress: "El Resultado: Progreso Compuesto",
    compounding_desc: "Cuando el entrenamiento y la nutrición trabajan juntos, no solo sumas resultados—los multiplicas. Mejor recuperación significa entrenamiento más duro. Entrenamiento más duro significa progreso más rápido. Así es como ocurren las transformaciones.",
    apply_science: "Cómo Aplicamos la Ciencia",
    apply_science_desc: "Los principios que impulsan cada plan de nutrición de VYTA",
    periodized_nutrition: "Nutrición Periodizada",
    periodized_desc: "Tus necesidades nutricionales cambian según la intensidad del entrenamiento, las demandas de recuperación y los objetivos. Ajustamos tus macros y horarios de comidas dinámicamente.",
    periodized_detail: "En días de entrenamiento, aumentamos la ingesta de carbohidratos para impulsar el rendimiento. En días de descanso, cambiamos hacia más proteína y grasa para recuperación y saciedad.",
    nutrient_timing: "Tiempo de Nutrientes",
    nutrient_timing_desc: "Cuándo comes importa casi tanto como qué comes. Optimizamos el horario de comidas alrededor de tu programa de entrenamiento.",
    nutrient_timing_detail: "Las comidas pre-entrenamiento están diseñadas para energía sostenida. La nutrición post-entrenamiento prioriza la síntesis de proteínas y la reposición de glucógeno.",
    micronutrient_optimization: "Optimización de Micronutrientes",
    micronutrient_desc: "Más allá de los macros, aseguramos que tus comidas proporcionen vitaminas, minerales y fitonutrientes esenciales para una salud óptima.",
    micronutrient_detail: "Cada plan de comidas se analiza para hierro, zinc, vitamina D, omega-3 y otros nutrientes críticos que a menudo faltan en las dietas modernas.",
    gut_health: "Integración de Salud Intestinal",
    gut_health_desc: "Un intestino saludable es fundamental para la absorción de nutrientes, la función inmune e incluso la salud mental.",
    gut_health_detail: "Incorporamos alimentos ricos en prebióticos y probióticos naturalmente en tus planes de comidas para apoyar tu microbioma.",
    science_backed_personal: "Respaldado por la Ciencia. Entregado Personalmente.",
    stop_guessing: "Deja de adivinar. Empieza a progresar. Deja que la IA impulsada por la investigación cree tu camino personalizado hacia los resultados.",
    start_science_journey: "Comienza Tu Viaje Científico",
    
    // Common UI elements
    minutes: "minutos",
    meals: "comidas",
    select: "Seleccionar",
    gym: "Gimnasio",
    home: "Casa",
    privacy: "Privacidad",
    terms: "Términos",
  },
  
  ja: {
    // Landing Page
    hero_title: "あなたのプラン。あなたのために。あなただけのために。",
    hero_subtitle: "エリートパフォーマンスプログラム、あなたに合わせてカスタマイズ。科学に基づいて。",
    hero_desc: "すべてのプログラムは100%ユニーク—あなたの体、目標、スケジュール、人生に合わせてカスタム構築。私たちが作成し、あなたが達成する。",
    get_started: "始める",
    login: "ログイン",
    start_free_trial: "無料トライアルを開始",
    watch_video: "動画を見る",
    
    // Navigation
    about: "私たちについて",
    science: "科学",
    dashboard: "ダッシュボード",
    training: "トレーニング",
    nutrition: "栄養",
    progress: "進捗",
    challenges: "チャレンジ",
    messages: "メッセージ",
    profile: "プロフィール",
    logout: "ログアウト",
    
    // For Everyone Section
    built_for_everyone: "すべての人のために",
    everyone_desc: "ジムに足を踏み入れたことがなくても、オリンピックの夢を追いかけていても、あなただけのプログラムを作成します。",
    complete_beginners: "完全初心者",
    beginners_desc: "運動したことがない？問題ありません。あなたの今いる場所から始めます—ソファからでも。",
    youth_athletes: "ユースアスリート",
    youth_desc: "生涯のアスリートとしての成功の基礎を築く、年齢に適したトレーニング。",
    active_adults: "アクティブな大人",
    adults_desc: "周期化された段階的トレーニングでフィットネスを次のレベルへ。",
    elite_athletes: "エリートアスリート",
    elite_desc: "精密な周期化とピークパフォーマンスに焦点を当てた競技レベルのプログラミング。",
    you_tell_us: "あなたが誰かを教えてください。",
    we_build: "必要なものを正確に構築します。",
    
    // 100% Custom Section
    custom_title: "100%カスタム。100%あなたのもの。",
    custom_desc: "ほとんどのフィットネスアプリは全員に同じプランを微調整して提供します。私たちは違います。すべてのプログラムはあなたの正確なプロフィールに基づいてゼロから生成されます。",
    zero_templates: "テンプレートゼロ。コピーゼロ。",
    we_analyze: "分析内容:",
    we_create: "作成内容:",
    you_bring: "あなたはコミットメントを持ってきます。",
    we_handle: "それ以外はすべて私たちが処理します。",
    data_points: "ユーザーごとに分析されるデータポイント",
    unique_combinations: "ユニークなプログラムの組み合わせ",
    
    // Cultural Nutrition
    culturally_intelligent: "文化的にインテリジェント",
    nutrition_respects: "あなたの文化を尊重する栄養",
    culture_desc: "西洋の味覚向けに作られた一般的な食事プランとは異なり、VYTAはあなたの食文化の伝統を祝う栄養プログラムを作成します。",
    cuisines_supported: "の料理の伝統と本格的なレシピ",
    auto_detect: "即座のパーソナライゼーションのために自動的に位置を検出",
    mix_cuisines: "多文化の味覚に合わせて料理をミックス",
    same_precision: "同じ精度のマクロ、あなたが求める馴染みのある味",
    
    // Features
    features_title: "必要なものすべて",
    feature_training: "パーソナライズドトレーニング",
    feature_training_desc: "AIパワードプログラム、目標、器具、スケジュールに適応。",
    feature_nutrition: "カスタム栄養プラン",
    feature_nutrition_desc: "好み、アレルギー、料理に合わせた食事プラン。",
    feature_progress: "進捗追跡",
    feature_progress_desc: "ビジュアルインサイトと分析で目標達成をサポート。",
    feature_challenges: "月間チャレンジ",
    feature_challenges_desc: "コミュニティ主導の競争でモチベーションを維持。",
    
    // CTA
    cta_title: "変わる準備はできましたか?",
    cta_subtitle: "今日から7日間の無料トライアルを始めましょう。",
    
    // Auth
    create_account: "アカウント作成",
    sign_in: "サインイン",
    email: "メールアドレス",
    password: "パスワード",
    confirm_password: "パスワード確認",
    name: "氏名",
    already_have_account: "すでにアカウントをお持ちですか?",
    dont_have_account: "アカウントをお持ちでないですか?",
    
    // Common
    loading: "読み込み中...",
    save: "保存",
    cancel: "キャンセル",
    next: "次へ",
    back: "戻る",
    submit: "送信",
    continue: "続ける",
    
    // Onboarding
    onboarding_welcome: "あなたの体験をパーソナライズしましょう",
    select_country: "国を選択",
    date_of_birth: "生年月日",
    preferred_cuisines: "好みの料理",
    cuisine_hint: "好きな料理を選択してください。あなたが気に入る食事プランを作成するのに役立ちます。",
    step: "ステップ",
    of: "/",
    
    // About Page
    our_story: "私たちのストーリー",
    making_health_accessible: "健康をすべての人に",
    about_intro: "私たちはシンプルな信念でVYTAを始めました：どこに住んでいても、何を払えても、どのように食べても、誰もが世界クラスのフィットネスと栄養指導を受ける価値があります。",
    our_mission: "私たちの使命",
    mission_text: "長い間、プレミアムフィットネスコーチングとパーソナライズされた栄養計画はエリートアスリートやパーソナルトレーナーを雇える人だけのものでした。私たちはそれを変えています。",
    
    // Science Page
    evidence_based: "エビデンスに基づくアプローチ",
    science_title: "あなたの変身を支える科学",
    science_subtitle: "私たちが行うすべての推奨事項は、査読済みの研究に基づいています。私たちのアプローチがなぜ機能するのか—そしてそれを証明する科学がここにあります。",
    cant_out_train: "悪い食事をトレーニングで補うことはできません",
    
    // Subscription
    subscription_title: "トライアルを開始",
    free_trial: "7日間無料トライアル",
    per_week: "/週",
    per_month: "/月",
    per_year: "/年",
    most_popular: "最も人気",
    select_plan: "プランを選択",
    
    // Footer
    all_rights: "全著作権所有。",
  },
  
  ko: {
    hero_title: "당신의 플랜. 당신만을 위해. 오직 당신만.",
    hero_subtitle: "엘리트 퍼포먼스 프로그램, 당신에게 맞춤화. 과학 기반.",
    hero_desc: "모든 프로그램은 100% 독특합니다—당신의 몸, 목표, 일정, 삶에 맞게 맞춤 제작됩니다. 우리가 만들고, 당신이 정복합니다.",
    get_started: "시작하기",
    login: "로그인",
    start_free_trial: "무료 체험 시작",
    about: "소개",
    science: "과학",
    dashboard: "대시보드",
    training: "트레이닝",
    nutrition: "영양",
    progress: "진행",
    challenges: "챌린지",
    messages: "메시지",
    profile: "프로필",
    logout: "로그아웃",
    built_for_everyone: "모든 사람을 위해",
    complete_beginners: "완전 초보자",
    youth_athletes: "청소년 운동선수",
    active_adults: "활동적인 성인",
    elite_athletes: "엘리트 운동선수",
    custom_title: "100% 맞춤. 100% 당신의 것.",
    features_title: "필요한 모든 것",
    cta_title: "변화할 준비가 되셨나요?",
    cta_subtitle: "오늘 7일 무료 체험을 시작하세요.",
    create_account: "계정 만들기",
    sign_in: "로그인",
    email: "이메일",
    password: "비밀번호",
    name: "이름",
    loading: "로딩 중...",
    save: "저장",
    cancel: "취소",
    next: "다음",
    back: "뒤로",
    all_rights: "모든 권리 보유.",
  },
  
  pt: {
    hero_title: "Seu Plano. Feito para Você. Só para Você.",
    hero_subtitle: "Programas de performance de elite, personalizados para você. Baseados em ciência.",
    hero_desc: "Cada programa é 100% único—construído sob medida para seu corpo, seus objetivos, sua agenda e sua vida. Nós criamos. Você conquista.",
    get_started: "Começar",
    login: "Entrar",
    start_free_trial: "Começar Teste Grátis",
    about: "Sobre",
    science: "Ciência",
    dashboard: "Painel",
    training: "Treino",
    nutrition: "Nutrição",
    progress: "Progresso",
    challenges: "Desafios",
    messages: "Mensagens",
    profile: "Perfil",
    logout: "Sair",
    built_for_everyone: "Feito para Todos",
    complete_beginners: "Iniciantes",
    youth_athletes: "Atletas Jovens",
    active_adults: "Adultos Ativos",
    elite_athletes: "Atletas Elite",
    custom_title: "100% Personalizado. 100% Seu.",
    features_title: "Tudo o que Você Precisa",
    cta_title: "Pronto para se Transformar?",
    cta_subtitle: "Comece seu teste gratuito de 7 dias hoje.",
    create_account: "Criar Conta",
    sign_in: "Entrar",
    email: "E-mail",
    password: "Senha",
    name: "Nome Completo",
    loading: "Carregando...",
    save: "Salvar",
    cancel: "Cancelar",
    next: "Próximo",
    back: "Voltar",
    all_rights: "Todos os direitos reservados.",
  },
  
  ru: {
    hero_title: "Ваш план. Создан для вас. Только для вас.",
    hero_subtitle: "Элитные программы, персонализированные для вас. На основе науки.",
    hero_desc: "Каждая программа на 100% уникальна—создана специально для вашего тела, целей, расписания и жизни. Мы создаём, вы покоряете.",
    get_started: "Начать",
    login: "Войти",
    start_free_trial: "Начать бесплатно",
    about: "О нас",
    science: "Наука",
    dashboard: "Панель",
    training: "Тренировки",
    nutrition: "Питание",
    progress: "Прогресс",
    challenges: "Вызовы",
    messages: "Сообщения",
    profile: "Профиль",
    logout: "Выйти",
    built_for_everyone: "Для всех",
    complete_beginners: "Начинающие",
    youth_athletes: "Молодые спортсмены",
    active_adults: "Активные взрослые",
    elite_athletes: "Элитные спортсмены",
    custom_title: "100% индивидуально. 100% ваше.",
    features_title: "Всё что нужно",
    cta_title: "Готовы к переменам?",
    cta_subtitle: "Начните 7-дневный бесплатный период сегодня.",
    create_account: "Создать аккаунт",
    sign_in: "Войти",
    email: "Email",
    password: "Пароль",
    name: "Полное имя",
    loading: "Загрузка...",
    save: "Сохранить",
    cancel: "Отмена",
    next: "Далее",
    back: "Назад",
    all_rights: "Все права защищены.",
  },
  
  de: {
    hero_title: "Ihr Plan. Für Sie gebaut. Nur für Sie.",
    hero_subtitle: "Elite-Leistungsprogramme, personalisiert für Sie. Wissenschaftlich fundiert.",
    hero_desc: "Jedes Programm ist 100% einzigartig—maßgeschneidert für Ihren Körper, Ihre Ziele, Ihren Zeitplan und Ihr Leben. Wir erstellen es, Sie erobern es.",
    get_started: "Loslegen",
    login: "Anmelden",
    start_free_trial: "Kostenlos testen",
    about: "Über uns",
    science: "Wissenschaft",
    dashboard: "Dashboard",
    training: "Training",
    nutrition: "Ernährung",
    progress: "Fortschritt",
    challenges: "Herausforderungen",
    messages: "Nachrichten",
    profile: "Profil",
    logout: "Abmelden",
    built_for_everyone: "Für jeden gemacht",
    complete_beginners: "Anfänger",
    youth_athletes: "Nachwuchsathleten",
    active_adults: "Aktive Erwachsene",
    elite_athletes: "Elite-Athleten",
    custom_title: "100% individuell. 100% Ihres.",
    features_title: "Alles was Sie brauchen",
    cta_title: "Bereit für die Veränderung?",
    cta_subtitle: "Starten Sie heute Ihre 7-tägige kostenlose Testphase.",
    create_account: "Konto erstellen",
    sign_in: "Anmelden",
    email: "E-Mail",
    password: "Passwort",
    name: "Vollständiger Name",
    loading: "Laden...",
    save: "Speichern",
    cancel: "Abbrechen",
    next: "Weiter",
    back: "Zurück",
    all_rights: "Alle Rechte vorbehalten.",
  },
  
  fr: {
    hero_title: "Votre Plan. Fait pour Vous. Rien que pour Vous.",
    hero_subtitle: "Programmes de performance d'élite, personnalisés pour vous. Basés sur la science.",
    hero_desc: "Chaque programme est 100% unique—construit sur mesure pour votre corps, vos objectifs, votre emploi du temps et votre vie. Nous créons, vous conquérez.",
    get_started: "Commencer",
    login: "Connexion",
    start_free_trial: "Essai gratuit",
    about: "À propos",
    science: "Science",
    dashboard: "Tableau de bord",
    training: "Entraînement",
    nutrition: "Nutrition",
    progress: "Progrès",
    challenges: "Défis",
    messages: "Messages",
    profile: "Profil",
    logout: "Déconnexion",
    built_for_everyone: "Fait pour tous",
    complete_beginners: "Débutants",
    youth_athletes: "Jeunes athlètes",
    active_adults: "Adultes actifs",
    elite_athletes: "Athlètes d'élite",
    custom_title: "100% personnalisé. 100% le vôtre.",
    features_title: "Tout ce dont vous avez besoin",
    cta_title: "Prêt à vous transformer?",
    cta_subtitle: "Commencez votre essai gratuit de 7 jours aujourd'hui.",
    create_account: "Créer un compte",
    sign_in: "Se connecter",
    email: "E-mail",
    password: "Mot de passe",
    name: "Nom complet",
    loading: "Chargement...",
    save: "Enregistrer",
    cancel: "Annuler",
    next: "Suivant",
    back: "Retour",
    all_rights: "Tous droits réservés.",
  },
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('vyta_language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('vyta_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, translations }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
